import { useState } from 'react'

const API = import.meta.env.VITE_API_URL || '/api'

function HexMark({ size = 30 }) {
  return (
    <svg
      viewBox="0 0 32 32" width={size} height={size} fill="none"
      aria-hidden="true"
      style={{ color: 'var(--tg-teal-400)', display: 'block' }}
    >
      <defs>
        <clipPath id="hexAI">
          <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" />
        </clipPath>
      </defs>
      <rect x="0" y="0" width="32" height="18.6" fill="currentColor" opacity="0.18" clipPath="url(#hexAI)" />
      <path d="M16 3 L27 9.5 L27 22.5 L16 29 L5 22.5 L5 9.5 Z" stroke="currentColor" strokeWidth="2.2" strokeLinejoin="round" />
      <line x1="6" y1="18.6" x2="26" y2="18.6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  )
}

function decodeToken(token) {
  try {
    return JSON.parse(atob(token.split('.')[1]))
  } catch {
    return null
  }
}

function PwMeter({ value }) {
  let score = 0
  if (value.length >= 10) score++
  if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++
  if (/\d/.test(value)) score++
  if (/[^A-Za-z0-9]/.test(value)) score++
  const colors = ['transparent', 'var(--tg-danger-500)', 'var(--tg-warning-500)', 'var(--tg-warning-500)', 'var(--tg-success-500)']
  const active = value ? Math.max(1, score) : 0
  return (
    <div style={{ display: 'flex', gap: 5, marginTop: 8 }}>
      {[1, 2, 3, 4].map(i => (
        <span key={i} style={{ flex: 1, height: 4, borderRadius: 999, background: i <= active ? colors[active] : 'var(--tg-graphite-700)', transition: 'background var(--tg-duration-base) var(--tg-ease-out)' }} />
      ))}
    </div>
  )
}

export default function AcceptInvite() {
  const params = new URLSearchParams(window.location.search)
  const token = params.get('token') || ''
  const decoded = decodeToken(token)

  const [password, setPassword] = useState('')
  const [name, setName] = useState(decoded?.name || '')
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [apiError, setApiError] = useState('')

  function validate() {
    const e = {}
    if (password.length < 8) e.password = 'Password must be at least 8 characters'
    return e
  }

  async function handleSubmit(ev) {
    ev.preventDefault()
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length) return
    setLoading(true)
    setApiError('')
    try {
      const res = await fetch(API + '/auth/accept-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, name: name.trim() || undefined }),
      })
      const data = await res.json()
      if (!res.ok) { setApiError(data.error || 'Failed to accept invite'); setLoading(false); return }
      localStorage.setItem('tg_token', data.token)
      localStorage.setItem('tg_user', JSON.stringify(data.user))
      window.location.href = '/'
    } catch (err) {
      setApiError(err.message)
      setLoading(false)
    }
  }

  if (!token) {
    return (
      <div className="auth-wrap" style={{ gridTemplateColumns: '1fr' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div style={{ width: '100%', maxWidth: 360, textAlign: 'center' }}>
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="var(--tg-danger-500)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 16, display: 'block', margin: '0 auto 16px' }}>
              <path d="M10.36 3.59L1.99 17.5A1.91 1.91 0 0 0 3.63 20.4h16.74a1.91 1.91 0 0 0 1.64-2.9L13.64 3.6a1.91 1.91 0 0 0-3.28 0z" />
              <path d="M12 9v4M12 17h.01" />
            </svg>
            <h2 style={{ fontSize: 'var(--tg-text-lg)', fontWeight: 'var(--tg-weight-semibold)', margin: '0 0 8px', color: 'var(--tg-text-primary)' }}>Invalid invite link</h2>
            <p style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)', margin: 0 }}>
              This invite link is missing or malformed. Please request a new one.
            </p>
          </div>
        </div>
      </div>
    )
  }

  const orgName = decoded?.org_name || 'Your organization'
  const orgInitials = orgName.split(/\s+/).map(w => w[0]).join('').slice(0, 2).toUpperCase()
  const email = decoded?.email || ''

  return (
    <div className="auth-wrap">
      {/* LEFT: brand panel */}
      <section
        className="brand-panel"
        style={{
          position: 'relative', overflow: 'hidden',
          padding: 'var(--tg-space-10)',
          flexDirection: 'column', justifyContent: 'space-between',
          background: `
            radial-gradient(120% 90% at 15% 0%, rgba(20,184,166,0.16) 0%, transparent 45%),
            linear-gradient(160deg, var(--tg-graphite-900) 0%, var(--tg-graphite-950) 70%)
          `,
          borderRight: '1px solid var(--tg-graphite-800)',
        }}
      >
        <div style={{
          position: 'absolute', inset: 0, opacity: 0.4,
          backgroundImage: 'linear-gradient(var(--tg-graphite-800) 1px, transparent 1px), linear-gradient(90deg, var(--tg-graphite-800) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          WebkitMaskImage: 'radial-gradient(120% 90% at 15% 0%, #000 0%, transparent 60%)',
          maskImage: 'radial-gradient(120% 90% at 15% 0%, #000 0%, transparent 60%)',
        }} />

        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 'var(--tg-space-3)' }}>
          <HexMark />
          <span style={{ fontSize: 'var(--tg-text-xl)', fontWeight: 'var(--tg-weight-medium)', letterSpacing: 'var(--tg-tracking-tight)' }}>Tankguard</span>
        </div>

        <div style={{ position: 'relative' }}>
          <h1 style={{ fontSize: 'var(--tg-text-3xl)', fontWeight: 'var(--tg-weight-medium)', letterSpacing: 'var(--tg-tracking-tight)', lineHeight: 'var(--tg-leading-tight)', margin: '0 0 var(--tg-space-4)', maxWidth: '16ch' }}>
            You've been invited to join the fleet.
          </h1>
          <p style={{ fontSize: 'var(--tg-text-md)', color: 'var(--tg-text-secondary)', lineHeight: 'var(--tg-leading-relaxed)', maxWidth: '38ch', margin: 0 }}>
            Set your password to activate your account and start monitoring fuel across your fleet.
          </p>
        </div>

        <div style={{ position: 'relative', fontSize: 'var(--tg-text-xs)', color: 'var(--tg-text-muted)', display: 'flex', gap: 'var(--tg-space-4)' }}>
          <span>© 2026 Tankguard GmbH</span>
          <a href="mailto:hello@tankguard.de?subject=Privacy%20policy%20request" style={{ color: 'var(--tg-text-muted)', textDecoration: 'none' }}>Privacy</a>
          <span>Data hosted in Germany</span>
        </div>
      </section>

      {/* RIGHT: form panel */}
      <section style={{ display: 'flex', flexDirection: 'column', padding: 'var(--tg-space-8)' }}>
        <div style={{ height: 44 }} />

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ width: '100%', maxWidth: 360 }}>
            {/* invitation banner */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--tg-space-3)', background: 'var(--tg-graphite-900)', border: '1px solid var(--tg-graphite-800)', borderRadius: 'var(--tg-radius-lg)', padding: 'var(--tg-space-3) var(--tg-space-4)', marginBottom: 'var(--tg-space-6)' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--tg-teal-700)', color: 'var(--tg-graphite-50)', display: 'grid', placeItems: 'center', fontWeight: 'var(--tg-weight-semibold)', fontSize: 'var(--tg-text-sm)', flexShrink: 0 }}>
                {orgInitials}
              </div>
              <span style={{ fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-secondary)' }}>
                <strong style={{ color: 'var(--tg-text-primary)', fontWeight: 'var(--tg-weight-medium)' }}>{orgName}</strong>{' '}
                invited you to join their fleet on Tankguard.
              </span>
            </div>

            <h2 style={{ fontSize: 'var(--tg-text-2xl)', fontWeight: 'var(--tg-weight-medium)', letterSpacing: 'var(--tg-tracking-tight)', margin: '0 0 6px' }}>
              Accept your invitation
            </h2>
            <p style={{ fontSize: 'var(--tg-text-base)', color: 'var(--tg-text-secondary)', margin: '0 0 var(--tg-space-6)' }}>
              {email ? `Set a password to activate your account for ${email}.` : 'Set a password to activate your account.'}
            </p>

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--tg-space-4)' }}>
              <div className="tg-field">
                <label className="tg-label" htmlFor="ai-name">Full name</label>
                <input
                  id="ai-name" className="tg-input" type="text"
                  value={name} onChange={e => setName(e.target.value)}
                  placeholder="Your name" autoComplete="name" autoFocus
                />
              </div>
              <div className="tg-field">
                <label className="tg-label" htmlFor="ai-pw">Create password</label>
                <input
                  id="ai-pw" className={`tg-input${errors.password ? ' is-error' : ''}`}
                  type="password" value={password}
                  onChange={e => setPassword(e.target.value)}
                  onBlur={() => setErrors(er => ({ ...er, ...validate() }))}
                  placeholder="At least 8 characters" autoComplete="new-password"
                />
                <PwMeter value={password} />
                {errors.password
                  ? <span className="tg-error">{errors.password}</span>
                  : <span className="tg-help">Use 8+ characters with a mix of letters, numbers, and symbols.</span>
                }
              </div>

              {apiError && (
                <p style={{ color: 'var(--tg-danger-500)', fontSize: 'var(--tg-text-sm)', margin: 0 }}>{apiError}</p>
              )}

              <button
                type="submit"
                className={`tg-btn tg-btn--lg tg-btn--primary${loading ? ' is-loading' : ''}`}
                disabled={loading || !password}
                style={{ width: '100%' }}
              >
                <span className="tg-spinner" aria-hidden="true" />
                {loading ? 'Setting up account…' : 'Activate account'}
              </button>
            </form>

            <div style={{ textAlign: 'center', fontSize: 'var(--tg-text-sm)', color: 'var(--tg-text-tertiary)', marginTop: 'var(--tg-space-6)' }}>
              Already have an account?{' '}
              <a href="/" style={{ color: 'var(--tg-text-accent)', textDecoration: 'none', fontWeight: 'var(--tg-weight-medium)' }}>Sign in</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
